import './Section.css';
import { motion, useScroll, useTransform, useSpring } from "framer-motion" // Cambiado a framer-motion para mayor compatibilidad
import { ChevronsDown, ChevronsUp  } from 'lucide-react-native';
import { useRef, useEffect, useState } from "react"
import { BookHeart } from 'lucide-react-native';
import properties from '../properties.json'
import imagen1 from '../assets/Cronologia/2014.avif';
import imagen2 from '../assets/Cronologia/2015.jpg';
import imagen3 from '../assets/Cronologia/2016.avif';
import imagen4 from '../assets/Cronologia/2017.avif';
import imagen5 from '../assets/Cronologia/2018.avif';
import imagen6 from '../assets/Cronologia/2019.avif';
import imagen7 from '../assets/Cronologia/2020.avif';
import imagen8 from '../assets/Cronologia/2021.avif';
import imagen9 from '../assets/Cronologia/2022.avif';
import imagen10 from '../assets/Cronologia/2023.avif';
import imagen11 from '../assets/Cronologia/2024.avif';
import imagen12 from '../assets/Cronologia/2025.avif';

export function Cronologia() {
    const containerRef = useRef<HTMLDivElement>(null)
    const [contentWidth, setContentWidth] = useState(0)
    const [windowWidth, setWindowWidth] = useState(0)

    // 1. Calculamos el ancho real del contenido al cargar y redimensionar
    useEffect(() => {
        const updateWidths = () => {
            if (containerRef.current) {
                const gallery = containerRef.current.querySelector('.gallery')
                if (gallery) {
                    setContentWidth(gallery.scrollWidth)
                    setWindowWidth(window.innerWidth)
                }
            }
        }

        updateWidths()
        window.addEventListener("resize", updateWidths)
        return () => window.removeEventListener("resize", updateWidths)
    }, [])

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"],
    })

    // 2. El desplazamiento final es la diferencia entre el total del contenido y lo que se ve en pantalla
    const xRange = -(contentWidth - windowWidth + 15 + (windowWidth > 600 ? 0 : 0))
    // Usamos useSpring para que el movimiento sea suave en touchscreens
    const xRaw = useTransform(scrollYProgress, [0, 1], [0, xRange])
    const x = useSpring(xRaw, { stiffness: 400, damping: 90 })

    return (
        <div>
            <section id="sectionCronologia" className="intro-section">
                <BookHeart size={32} color={properties.detailsColor} />
                <h2 className='title'>Nuestra Historia</h2>
                <div className='line' />
            </section>

            <div ref={containerRef} className="scroll-container">
                <div className="sticky-wrapper">
                    <motion.div className="gallery" style={{ x }}>
                        <p style={{ textAlign: 'center', paddingLeft: 15, marginTop: 50, color: '#4b5563'}}>Desliza hacia abajo <ChevronsDown color='#4b5563'/></p>
                        {items.map((item) => (
                            <div
                                key={item.id}
                                className="gallery-item"
                                style={{
                                    "--item-color": item.color,
                                    "--item-image": `url(${item.image})`,
                                } as React.CSSProperties}
                            >
                                <div className="item-content">
                                    <h2>{item.label}</h2>
                                </div>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </div>

            <section className="outro-section">
                <p style={{ textAlign: 'center', paddingLeft: 15, marginTop: 50, color: '#4b5563'}}>Desliza hacia arriba <ChevronsUp color='#4b5563'/></p>
            </section>

            <StyleSheet />
        </div>
    )
}

function StyleSheet() {
    return (
        <style>{`
            body { overflow-x: hidden; margin: 0; background: #f9f6f7; }
            #example { overflow: visible; }
            
            .intro-section {
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: #f9f6f7;
                flex-direction: column;
                marginTop: 50px;
                padding-top: 50px;
                scroll-margin-top: 65px;
            }

            .scroll-container {
                height: 400vh; /* Más altura = scroll más lento y suave */
                position: relative;
            }

            .sticky-wrapper {
                position: sticky;
                top: 0;
                height: 100vh;
                width: 100%; /* Ocupa todo el ancho */
                display: flex;
                align-items: center;
                overflow: hidden; /* Evita scrollbar horizontal */
            }

            .gallery {
                display: flex;
                gap: 40px;
                padding: 0px 5vw; /* Margen inicial para que la primera foto no esté pegada */
                will-change: transform;
                align-items: center;
            }

            .gallery-item {
                flex-shrink: 0;
                width: 70svw; /* Ancho relativo al dispositivo */
                max-width: 400px;
                height: 80vh;
                margin-top: 10vh;
                border-radius: 0.75rem;
                position: relative;
                overflow: hidden;
                background-image: var(--item-image);
                background-size: cover;
                background-position: center;
                background-repeat: no-repeat; 
            }

            @media (max-width: 600px) {
                .gallery { padding-top:90px; gap: 20px; padding: 0 5vw; }
                .gallery-item { height: 80vh; width: 85vw; }
                .intro-section h1 { font-size: 40px; }
            }

            .gallery-item::before {
                content: "";
                position: absolute;
                inset: 0;
                background: linear-gradient(
                    to bottom,
                    transparent 60%,
                    var(--item-color)
                );
                mix-blend-mode: multiply;
            }

            .item-content { position: absolute; bottom: 30px; left: 30px; z-index: 1; }
            .item-number { color: var(--item-color); font-family: monospace; }
            .gallery-item h2 { color: white; margin: 0; font-size: 24px; }
            .outro-section { height: auto; display: flex; justify-content: center; align-items: center; color: black; }

            .scroll-down {
            position: absolute; /* Se fija respecto al padre */
            top: 150px;       /* Distancia desde el borde inferior del div */
            left: 50%;
            transform: translateX(-50%);
        `}</style>
    )
}

const items = [
    { id: 1, color: "#ff0088", label: "2014", image: imagen1 },
    { id: 2, color: "#dd00ee", label: "2015", image: imagen2 },
    { id: 3, color: "#9911ff", label: "2016", image: imagen3 },
    { id: 4, color: "#0d63f8", label: "2017", image: imagen4 },
    { id: 5, color: "#e0f70c", label: "2018", image: imagen5 },
    { id: 6, color: "#c00cf7", label: "2019", image: imagen6 },
    { id: 7, color: "#f70c14", label: "2020", image: imagen7 },
    { id: 8, color: "#0c14f7", label: "2021", image: imagen8 },
    { id: 9, color: "#20f70c", label: "2022", image: imagen9 },
    { id: 10, color: "#0cdcf7", label: "2023", image: imagen10 },
    { id: 11, color: "#f70ce0", label: "2024", image: imagen11 },
    { id: 12, color: "#0cf72b", label: "2025", image: imagen12 }
]